// Tem que rodar antes de qualquer código que injete <style> — é o primeiro import de index.tsx e
// vive num chunk sem dependência (vite.config.js manualChunks) pra ser avaliado antes do vendor.

declare global {
  interface Window {
    __nonce__?: string;
    EMOTION_DISABLE_SPEEDY?: boolean;
  }
}

// Read nonce from meta tag (Lambda replaces NONCE_PLACEHOLDER with the real nonce)
function getCspNonce() {
  if (typeof document === 'undefined') return undefined;
  const el = document.querySelector('meta[name="csp-nonce"]');
  const nonce = el?.getAttribute('content');
  return nonce;
}

const nonce = getCspNonce();
// Expose for any code that needs the active nonce
window.__nonce__ = nonce;

// Force Emotion to NEVER use speedy mode (critical for CSP).
// Speedy mode uses insertRule() which does not respect nonce attributes.
window.EMOTION_DISABLE_SPEEDY = true;

if (nonce) {
  // Add the nonce to a <style> / <link rel="stylesheet"> element if it lacks one.
  const stampNonce = (node) => {
    if (!node || node.nodeType !== 1 || node.nonce) return;
    const tag = node.tagName;
    if (
      tag === 'STYLE' ||
      (tag === 'LINK' && /stylesheet/i.test(node.getAttribute('rel') || ''))
    ) {
      node.setAttribute('nonce', nonce);
    }
  };

  // 1) Stamp at creation time — covers document.createElement('style').
  const originalCreateElement = document.createElement.bind(document);
  document.createElement = function (tagName, options) {
    const element = originalCreateElement(tagName, options);
    if (typeof tagName === 'string' && tagName.toLowerCase() === 'style') {
      element.setAttribute('nonce', nonce);
    }
    return element;
  };

  // 2) Stamp at insertion time — the catch-all. Some libraries insert an EMPTY
  //    <style> and fill it afterwards (e.g. styleInject), or build the element
  //    via paths the createElement patch does not see. CSP is evaluated when the
  //    node is connected, so the nonce must be present BEFORE the original insert.
  // appendChild / insertBefore take a single node as the first argument.
  const patchInsert = (proto, method) => {
    const original = proto[method];
    if (!original) return;
    proto[method] = function (node, ...rest) {
      stampNonce(node);
      return original.call(this, node, ...rest);
    };
  };
  patchInsert(Node.prototype, 'appendChild');
  patchInsert(Node.prototype, 'insertBefore');

  // append / prepend / before / after (ParentNode/ChildNode) accept any number
  // of Node or string arguments — stamp every Node argument. Some third-party
  // scripts (e.g. Hotjar) add their <style> via head.append(...).
  const patchVarargInsert = (proto, method) => {
    const original = proto?.[method];
    if (!original) return;
    proto[method] = function (...nodes) {
      for (const n of nodes) stampNonce(n);
      return original.apply(this, nodes);
    };
  };
  patchVarargInsert(Element.prototype, 'append');
  patchVarargInsert(Element.prototype, 'prepend');
  patchVarargInsert(Element.prototype, 'before');
  patchVarargInsert(Element.prototype, 'after');
  patchVarargInsert(Document.prototype, 'append');
  patchVarargInsert(Document.prototype, 'prepend');

  console.info('[CSP Init] Nonce patches installed (createElement + insertion)');
  console.info('[CSP Init] Emotion speedy mode disabled');
} else {
  console.warn('[CSP Init] No nonce found - CSP may block styles');
}

export { nonce, getCspNonce };
